import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Button from "react-md/lib/Buttons/Button";
import FontIcon from "react-md/lib/FontIcons";
import { Select, MenuItem } from "@material-ui/core";
import moment from "moment";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import RiskStratificationService from "../service/RiskStratificationService";
import AuthenticationService from "service/auth/AuthenticationService";
import TrendChartDialog from "./TrendChartDialog";
import backArrowIcon from "../../../../../assets/icons/back_left_arrow_orange.png";
import rightArrowRed from "../../../../../assets/icons/right_arrow_red.png";
import circleCheckIcon from "../../../../../assets/icons/ circle_check_icon.png";
import circleCrossIcon from "../../../../../assets/icons/circlw_cross_icon.png";
import arrowUpRed from "../../../../../assets/icons/arrow_up_red.png";
import "../styles/riskDetail.scss";

const service = new RiskStratificationService();
const authenticationService = new AuthenticationService();

const RISK_LEVEL_CLASS_MAP = {
  1: "life-threatening",
  2: "very-high",
  3: "high",
  4: "moderate",
  5: "low",
};

const RISK_LEVEL_LABEL_MAP = {
  1: "Life Threatening Risk",
  2: "Very High Risk",
  3: "High Risk",
  4: "Moderate Risk",
  5: "Low Risk",
};

const EVAL_STATUS_LABEL_MAP = {
  1: "Pending",
  2: "Under Review",
  3: "Correct",
  4: "Incorrect",
};

function getRiskClass(level) {
  return RISK_LEVEL_CLASS_MAP[level] || "";
}

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function riskSummaryToHtml(summary) {
  const text = (summary || "").trim();
  if (!text) return "";
  return `<p>${escapeHtml(text)}</p>`;
}

function keyDriversToHtml(drivers) {
  if (!Array.isArray(drivers) || drivers.length === 0) return "";
  const items = drivers
    .map((d) => (d == null ? "" : String(d).trim()))
    .filter(Boolean)
    .map((d) => `<li>${escapeHtml(d)}</li>`)
    .join("");
  return items ? `<ul>${items}</ul>` : "";
}

function recommendedActionsToHtml(actions) {
  if (!Array.isArray(actions) || actions.length === 0) return "";
  const items = actions
    .map((a) => {
      const text = (a?.action || "").trim();
      if (!text) return "";
      const tags = Array.isArray(a?.tags)
        ? a.tags.filter(Boolean).map((t) => escapeHtml(String(t))).join(", ")
        : "";
      return tags
        ? `<li>${escapeHtml(text)} <em>(${tags})</em></li>`
        : `<li>${escapeHtml(text)}</li>`;
    })
    .filter(Boolean)
    .join("");
  return items ? `<ul>${items}</ul>` : "";
}

function htmlToEditorState(html) {
  if (!html) return null;
  const block = htmlToDraft(html);
  if (!block) return null;
  return EditorState.createWithContent(
    ContentState.createFromBlockArray(block.contentBlocks),
  );
}

function getRiskLabel(level) {
  return RISK_LEVEL_LABEL_MAP[level] || "";
}

function getTagClass(tag) {
  if (!tag) return "";
  const s = tag.toLowerCase();
  if (s.includes("critical")) return "tag-critical";
  if (s.includes("elevated")) return "tag-elevated";
  if (s.includes("low")) return "tag-low";
  if (s.includes("normal")) return "tag-normal";
  return "tag-default";
}

function getTrendIcon(tag) {
  if (!tag) return "trending_flat";
  const s = tag.toLowerCase();
  if (s.includes("elevated") || s.includes("high") || s.includes("critical"))
    return "trending_up";
  if (s.includes("low")) return "trending_down";
  return "trending_flat";
}

function getTrendClass(tag) {
  if (!tag) return "";
  const s = tag.toLowerCase();
  if (s.includes("elevated") || s.includes("high") || s.includes("critical"))
    return "trend-up";
  if (s.includes("low")) return "trend-down";
  return "";
}

function getValueColorClass(tag) {
  if (!tag) return "value-default";
  const s = tag.toLowerCase();
  if (s.includes("elevated") || s.includes("high") || s.includes("critical"))
    return "value-elevated";
  if (s.includes("low")) return "value-lower";
  if (s.includes("normal")) return "value-normal";
  return "value-default";
}

function getPriorityClass(tags) {
  if (!tags || tags.length === 0) return "";
  const joined = tags.join(" ").toLowerCase();
  if (joined.includes("critical")) return "priority-critical";
  if (joined.includes("high")) return "priority-high";
  return "priority-default";
}

function getPatientAge(info) {
  if (!info) return null;
  const dob = info.dateOfBirth || info.dob;
  if (!dob) return null;
  const m = moment(dob);
  if (!m.isValid()) return null;
  return moment().diff(m, "years");
}

function getPatientGender(info) {
  if (!info) return "";
  if (info.gender === 1 || info.gender === "1") return "Female";
  if (info.gender === 2 || info.gender === "2") return "Male";
  if (typeof info.gender === "string") return info.gender;
  return "";
}

function getPatientBMI(info) {
  if (!info) return "";
  if (info.bmi) return Number(info.bmi).toFixed(1);
  const heightCm = parseFloat(info.height);
  const weightKg = parseFloat(info.weight);
  if (!heightCm || !weightKg) return "";
  const m = heightCm / 100;
  if (m <= 0) return "";
  return (weightKg / (m * m)).toFixed(1);
}

function RiskDetail(props) {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [evaluationStatus, setEvaluationStatus] = useState(0);
  const [evaluationReason, setEvaluationReason] = useState("");
  const [updatedRiskLevel, setUpdatedRiskLevel] = useState(0);
  const [evaluatorSummary, setEvaluatorSummary] = useState(
    EditorState.createEmpty(),
  );
  const [keyRiskDrivers, setKeyRiskDrivers] = useState(
    EditorState.createEmpty(),
  );
  const [evaluatorRecommendations, setEvaluatorRecommendations] = useState(
    EditorState.createEmpty(),
  );
  const [saving, setSaving] = useState(false);
  // Synchronous double-click guard. `saving` state alone has a race window:
  // rapid clicks can fire before React commits the `disabled` attribute to
  // the DOM. This ref short-circuits handleSave re-entry instantly.
  const savingRef = useRef(false);
  const [trendChartOpen, setTrendChartOpen] = useState(false);
  const [trendChartItem, setTrendChartItem] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [prevSummaryOpen, setPrevSummaryOpen] = useState(false);
  const [prevSummaryList, setPrevSummaryList] = useState([]);
  const [prevSummaryLoading, setPrevSummaryLoading] = useState(false);
  const [audits, setAudits] = useState([]);
  const [comparisonAuditId, setComparisonAuditId] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [activityLogOpen, setActivityLogOpen] = useState(false);

  const currentEmployeeId =
    authenticationService.getUserProfile()?.employeeProfile?.id || 0;
  const canEvaluate =
    record && record.evaluatedBy && currentEmployeeId === record.evaluatedBy;
  const hasPreviousEvaluation =
    record &&
    (record.evaluationStatus === 3 || record.evaluationStatus === 4);
  const evaluationOnTop = canEvaluate && hasPreviousEvaluation;

  const handleCardClick = (item) => {
    setTrendChartItem(item);
    setTrendChartOpen(true);
    setTrendData([]);

    if (!record?.patientId || !item.paramCode) {
      setTrendData([
        {
          date: item.recordDate || new Date().toISOString(),
          value: parseFloat(item.value) || 0,
        },
      ]);
      return;
    }

    setTrendLoading(true);

    // For labTest type, use the lab test tracking API
    if (item.type === "labTest") {
      service.fetchLabTestHistory(
        record.patientId,
        (res) => {
          setTrendLoading(false);
          if (res && Array.isArray(res)) {
            // Each entry has labTest[] -> subTest[] — find matching paramCode
            const dataPoints = [];
            res.forEach((doc) => {
              if (doc.labtest && Array.isArray(doc.labtest)) {
                doc.labtest.forEach((lt) => {
                  if (lt.subTest && Array.isArray(lt.subTest)) {
                    lt.subTest.forEach((st) => {
                      const stCode = st.subTestCode?.value || st.subTest;
                      const stLabel = st.subTestCode?.label || "";
                      const stLoinc = st.loincCode || "";
                      const matchCode = item.paramCode;
                      if (
                        stCode === matchCode ||
                        stLabel === matchCode ||
                        stLoinc === matchCode
                      ) {
                        const dateMs =
                          lt.testConductedDate || doc.testConductedDate;
                        dataPoints.push({
                          date: dateMs
                            ? new Date(dateMs).toISOString()
                            : doc.recordDate || doc.createdOn,
                          value: parseFloat(st.value) || 0,
                        });
                      }
                    });
                  }
                });
              }
            });
            dataPoints.sort((a, b) => new Date(a.date) - new Date(b.date));
            setTrendData(
              dataPoints.length > 0
                ? dataPoints
                : [
                    {
                      date: item.recordDate || new Date().toISOString(),
                      value: parseFloat(item.value) || 0,
                    },
                  ],
            );
          } else {
            setTrendData([
              {
                date: item.recordDate || new Date().toISOString(),
                value: parseFloat(item.value) || 0,
              },
            ]);
          }
        },
        (err) => {
          setTrendLoading(false);
          console.error("Error fetching lab test trend data:", err);
          setTrendData([
            {
              date: item.recordDate || new Date().toISOString(),
              value: parseFloat(item.value) || 0,
            },
          ]);
        },
      );
      return;
    }

    // For vitals, use the vitals range API
    const toDate = moment().valueOf().toString();
    const fromDate = moment().subtract(3, "years").valueOf().toString();
    service.fetchVitalsTrend(
      record.patientId,
      fromDate,
      toDate,
      (res) => {
        setTrendLoading(false);
        if (res && Array.isArray(res)) {
          // Response is array of vital groups: { vital, readings: [{date, type, value, unit, range}] }
          const dataPoints = [];
          res.forEach((group) => {
            if (group.readings && Array.isArray(group.readings)) {
              group.readings.forEach((r) => {
                if (r.type === item.paramCode || r.type === item.paramName) {
                  dataPoints.push({
                    date: r.date,
                    value: parseFloat(r.value) || 0,
                  });
                }
              });
            }
          });
          dataPoints.sort((a, b) => new Date(a.date) - new Date(b.date));
          setTrendData(
            dataPoints.length > 0
              ? dataPoints
              : [
                  {
                    date: item.recordDate || new Date().toISOString(),
                    value: parseFloat(item.value) || 0,
                  },
                ],
          );
        } else {
          setTrendData([
            {
              date: item.recordDate || new Date().toISOString(),
              value: parseFloat(item.value) || 0,
            },
          ]);
        }
      },
      (err) => {
        setTrendLoading(false);
        console.error("Error fetching trend data:", err);
        setTrendData([
          {
            date: item.recordDate || new Date().toISOString(),
            value: parseFloat(item.value) || 0,
          },
        ]);
      },
    );
  };

  const recordId = props.data?.id || props.data?.ID || props.data?._id;

  const handleViewPreviousSummaries = () => {
    if (!record?.patientId) return;
    setPrevSummaryOpen(true);
    setPrevSummaryLoading(true);
    service.fetchPatientHistory(
      record.patientId,
      recordId,
      (res) => {
        setPrevSummaryLoading(false);
        setPrevSummaryList(Array.isArray(res) ? res : []);
      },
      (err) => {
        setPrevSummaryLoading(false);
        console.error("Error fetching previous summaries:", err);
        setPrevSummaryList([]);
      },
    );
  };

  const handlePrevSummaryClick = (item) => {
    setPrevSummaryOpen(false);
    fetchDetail(item.id);
  };

  useEffect(() => {
    if (recordId) {
      fetchDetail(recordId);
      fetchAudits(recordId);
    } else {
      setLoading(false);
    }
  }, [recordId]);

  const isDialogOpen =
    trendChartOpen || prevSummaryOpen || comparisonAuditId !== null;

  useEffect(() => {
    if (!isDialogOpen) return;
    const body = document.body;
    const html = document.documentElement;
    const prevBody = body.style.overflow;
    const prevHtml = html.style.overflow;
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    return () => {
      body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
    };
  }, [isDialogOpen]);

  const fetchAudits = (id) => {
    service.fetchAudits(
      id,
      (res) => {
        setAudits(Array.isArray(res) ? res : []);
      },
      (err) => {
        console.error("Error fetching audits:", err);
        setAudits([]);
      },
    );
  };

  useEffect(() => {
    if (!record?.patientId) return;
    service.fetchPatientDetails(
      record.patientId,
      (res) => setPatientInfo(res || null),
      (err) => {
        console.error("Error fetching patient details:", err);
        setPatientInfo(null);
      },
    );
  }, [record?.patientId]);

  const fetchDetail = (id) => {
    setLoading(true);
    service.fetchDetail(
      id,
      (res) => {
        if (res) {
          setRecord(res);
          setEvaluationStatus(res.evaluationStatus || 0);
          setEvaluationReason(res.evaluationReason || "");
          setUpdatedRiskLevel(res.updatedRiskLevel || res.riskLevel || 0);

          const isCurrentEvaluator =
            !!res.evaluatedBy && currentEmployeeId === res.evaluatedBy;

          if (res.evaluatorSummary) {
            const state = htmlToEditorState(res.evaluatorSummary);
            if (state) setEvaluatorSummary(state);
          } else if (isCurrentEvaluator) {
            const state = htmlToEditorState(riskSummaryToHtml(res.riskSummary));
            if (state) setEvaluatorSummary(state);
          }

          if (res.keyRiskDriversHtml) {
            const state = htmlToEditorState(res.keyRiskDriversHtml);
            if (state) setKeyRiskDrivers(state);
          } else if (isCurrentEvaluator) {
            const state = htmlToEditorState(keyDriversToHtml(res.keyDrivers));
            if (state) setKeyRiskDrivers(state);
          }

          if (res.evaluatorRecommendations) {
            const state = htmlToEditorState(res.evaluatorRecommendations);
            if (state) setEvaluatorRecommendations(state);
          } else if (isCurrentEvaluator) {
            const state = htmlToEditorState(
              recommendedActionsToHtml(res.recommendedActions),
            );
            if (state) setEvaluatorRecommendations(state);
          }
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching detail:", err);
        setLoading(false);
      },
    );
  };

  const isEditorEmpty = (editorState) => {
    const content = editorState.getCurrentContent();
    return !content.hasText();
  };

  const handleSave = () => {
    if (savingRef.current) return;
    if (!evaluationStatus) return;
    if (evaluationStatus === 4) {
      if (
        !updatedRiskLevel ||
        !evaluationReason.trim() ||
        isEditorEmpty(evaluatorSummary) ||
        isEditorEmpty(keyRiskDrivers) ||
        isEditorEmpty(evaluatorRecommendations)
      ) {
        alert(
          "Please fill all mandatory fields: Risk level, Reason, Evaluator Summary, Key Risk Drivers, and Recommendations.",
        );
        return;
      }
    }
    savingRef.current = true;
    setSaving(true);
    const payload = {
      evaluationStatus,
      evaluationReason,
      updatedRiskLevel,
      evaluatorSummary: draftToHtml(
        convertToRaw(evaluatorSummary.getCurrentContent()),
      ),
      keyRiskDriversHtml: draftToHtml(
        convertToRaw(keyRiskDrivers.getCurrentContent()),
      ),
      evaluatorRecommendations: draftToHtml(
        convertToRaw(evaluatorRecommendations.getCurrentContent()),
      ),
    };
    service.saveEvaluation(
      recordId,
      payload,
      (res) => {
        savingRef.current = false;
        setSaving(false);
        fetchDetail(recordId);
        fetchAudits(recordId);
        if (props.markDashboardDirty) props.markDashboardDirty();
      },
      (err) => {
        console.error("Error saving evaluation:", err);
        savingRef.current = false;
        setSaving(false);
      },
    );
  };

  const handleClose = () => {
    if (props.history) {
      props.history.push("/miscellaneous/risk-stratification/dashboard");
    }
  };

  if (loading) {
    return (
      <div className="risk-detail-container">
        <div className="risk-detail-header">
          <div className="risk-detail-back" onClick={handleClose}>
            <img src={backArrowIcon} alt="back" />
          </div>
          <h2 className="risk-detail-title">AI report</h2>
        </div>
        <div className="risk-detail-loading">Loading...</div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="risk-detail-container">
        <div className="risk-detail-header">
          <div className="risk-detail-back" onClick={handleClose}>
            <img src={backArrowIcon} alt="back" />
          </div>
          <h2 className="risk-detail-title">AI report</h2>
        </div>
        <div className="risk-detail-loading">Record not found</div>
      </div>
    );
  }

  const riskClass = getRiskClass(record.riskLevel);

  // Group clinical data
  const vitalSigns = (record.clinicalData || []).filter(
    (d) => d.type === "vital",
  );
  const labValues = (record.clinicalData || []).filter(
    (d) => d.type === "labTest",
  );
  const diagnosisData = (record.clinicalData || []).filter(
    (d) => d.type === "diagnosis",
  );
  const chiefComplaints = (record.clinicalData || []).filter(
    (d) => d.type === "chiefComplaint",
  );
  const hospitalisationData = (record.clinicalData || []).filter(
    (d) => d.type === "hospitalisation",
  );

  // Data coverage sources. Prefer the backend-computed record.dataCoverage
  // (true bucket-fetch outcome). Fall back to deriving from clinicalData
  // for historical records persisted before dataCoverage existed.
  const backendCoverage = record.dataCoverage;
  const clinicalDataTypes = (record.clinicalData || []).map((d) => d.type);
  const dataCoverage = [
    { label: "Lab reports", key: "labTest" },
    { label: "Vital signs", key: "vital" },
    { label: "Diagnosis history", key: "diagnosis" },
    { label: "Family history", key: "familyHistory" },
    { label: "Chief complaints", key: "chiefComplaint" },
    { label: "Medication records", key: "medicine" },
    { label: "Hospitalisation history", key: "hospitalisation" },
  ];

  const evaluationSection = (
    <div className="risk-detail-section evaluation-section">
      <h3 className="section-title">Report Evaluation</h3>
      <p className="evaluation-status-text">
        Current status:{" "}
        {EVAL_STATUS_LABEL_MAP[record.evaluationStatus] || "Pending"}
      </p>
      {record.evaluatorName && (
        <p className="evaluator-name">
          Evaluator name: {record.evaluatorName}
        </p>
      )}
      <p className="evaluation-question">
        Is the AI risk assessment correct?
      </p>
      <div className="evaluation-buttons">
        <button
          className={`eval-btn ${evaluationStatus === 3 ? "active-correct" : ""}`}
          onClick={() => setEvaluationStatus(3)}
        >
          Correct
        </button>
        <button
          className={`eval-btn ${evaluationStatus === 4 ? "active-incorrect" : ""}`}
          onClick={() => setEvaluationStatus(4)}
        >
          Incorrect
        </button>
      </div>

      {evaluationStatus === 4 && (
        <>
          <div className="evaluation-risk-level">
            <label>Update risk level*</label>
            <Select
              value={updatedRiskLevel}
              onChange={(e) =>
                setUpdatedRiskLevel(parseInt(e.target.value, 10) || 0)
              }
              displayEmpty
              disableUnderline
              className="risk-level-select"
            >
              <MenuItem value={0} disabled>
                Select
              </MenuItem>
              <MenuItem value={5}>Low Risk</MenuItem>
              <MenuItem value={4}>Moderate Risk</MenuItem>
              <MenuItem value={3}>High Risk</MenuItem>
              <MenuItem value={2}>Very High Risk</MenuItem>
              <MenuItem value={1}>Life Threatening Risk</MenuItem>
            </Select>
          </div>

          <div className="evaluation-reason">
            <label>Reason for incorrect evaluation*</label>
            <textarea
              placeholder="Explain why the AI assessment is incorrect..."
              value={evaluationReason}
              onChange={(e) => setEvaluationReason(e.target.value)}
              maxLength={500}
            />
            <span className="char-count">max 500 words</span>
          </div>
        </>
      )}

      <div className="evaluation-editor-section">
        <h4 className="editor-label">
          Evaluator Summary
          {evaluationStatus === 4 && (
            <span className="mandatory-star">*</span>
          )}
        </h4>
        <Editor
          editorState={evaluatorSummary}
          wrapperClassName="risk-editor-wrapper"
          editorClassName="risk-editor-content"
          toolbar={{
            options: [
              "inline",
              "blockType",
              "fontSize",
              "fontFamily",
              "list",
              "textAlign",
              "link",
              "image",
              "history",
            ],
            inline: {
              options: [
                "bold",
                "italic",
                "underline",
                "strikethrough",
                "monospace",
                "superscript",
                "subscript",
              ],
            },
            list: {
              options: ["unordered", "ordered", "indent", "outdent"],
            },
          }}
          onEditorStateChange={(state) => setEvaluatorSummary(state)}
          placeholder="Type evaluator summary here..."
        />
      </div>

      <div className="evaluation-editor-section">
        <h4 className="editor-label">
          Key Risk Drivers
          {evaluationStatus === 4 && (
            <span className="mandatory-star">*</span>
          )}
        </h4>
        <Editor
          editorState={keyRiskDrivers}
          wrapperClassName="risk-editor-wrapper"
          editorClassName="risk-editor-content"
          toolbar={{
            options: [
              "inline",
              "blockType",
              "fontSize",
              "fontFamily",
              "list",
              "textAlign",
              "link",
              "image",
              "history",
            ],
            inline: {
              options: [
                "bold",
                "italic",
                "underline",
                "strikethrough",
                "monospace",
                "superscript",
                "subscript",
              ],
            },
            list: {
              options: ["unordered", "ordered", "indent", "outdent"],
            },
          }}
          onEditorStateChange={(state) => setKeyRiskDrivers(state)}
          placeholder="Type key risk drivers here..."
        />
      </div>

      <div className="evaluation-editor-section">
        <h4 className="editor-label">
          Evaluator recommendations
          {evaluationStatus === 4 && (
            <span className="mandatory-star">*</span>
          )}
        </h4>
        <Editor
          editorState={evaluatorRecommendations}
          wrapperClassName="risk-editor-wrapper"
          editorClassName="risk-editor-content"
          toolbar={{
            options: [
              "inline",
              "blockType",
              "fontSize",
              "fontFamily",
              "list",
              "textAlign",
              "link",
              "image",
              "history",
            ],
            inline: {
              options: [
                "bold",
                "italic",
                "underline",
                "strikethrough",
                "monospace",
                "superscript",
                "subscript",
              ],
            },
            list: {
              options: ["unordered", "ordered", "indent", "outdent"],
            },
          }}
          onEditorStateChange={(state) =>
            setEvaluatorRecommendations(state)
          }
          placeholder="Type evaluator recommendations here..."
        />
      </div>
    </div>
  );

  return (
    <div className="risk-detail-container">
      {/* Header */}
      <div className="risk-detail-header">
        <div className="risk-detail-header-left">
          <div className="risk-detail-back" onClick={handleClose}>
            <img src={backArrowIcon} alt="back" />
          </div>
          <h2 className="risk-detail-title">AI report</h2>
        </div>
        <div className="risk-detail-header-right">
          {canEvaluate && (
            <button className="save-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          )}
          <button className="close-btn" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        className={`risk-detail-content${isDialogOpen ? " no-scroll" : ""}`}
      >
        {/* Patient Info Bar */}
        <div className="risk-detail-patient-bar">
          {record.patientId ? (
            <Link
              to={`/patient-record/${record.patientId}`}
              className="patient-id-link"
            >
              PID-{record.patientId}
            </Link>
          ) : (
            <span className="patient-id">-</span>
          )}
          <span className="patient-meta">
            Creation Date:{" "}
            {record.triggeredAt
              ? moment(record.triggeredAt).format("DD MMM YYYY")
              : "-"}
          </span>
          {getPatientAge(patientInfo) != null && (
            <span className="patient-meta">
              Age: {getPatientAge(patientInfo)}
            </span>
          )}
          {getPatientGender(patientInfo) && (
            <span className="patient-meta">
              Gender: {getPatientGender(patientInfo)}
            </span>
          )}
          {getPatientBMI(patientInfo) && (
            <span className="patient-meta">
              BMI: {getPatientBMI(patientInfo)}
            </span>
          )}
        </div>

        {/* Report Evaluation (shown on top when prior evaluation exists) */}
        {evaluationOnTop && evaluationSection}

        {/* AI Risk Summary */}
        <div className="risk-detail-section">
          <div className="section-header-row">
            <h3 className="section-title">AI Risk Summary</h3>
            <span className={`risk-badge ${riskClass}`}>
              {getRiskLabel(record.riskLevel) || "N/A"}
            </span>
            <button
              className="view-prev-summary-btn"
              onClick={handleViewPreviousSummaries}
            >
              View previous AI summary
            </button>
          </div>
          <p className="risk-summary-text">{record.riskSummary}</p>

          {/* Key Risk Drivers */}
          {record.keyDrivers && record.keyDrivers.length > 0 && (
            <div className="key-drivers">
              <h4>Key Risk Drivers</h4>
              <ul>
                {record.keyDrivers.map((driver, idx) => (
                  <li key={idx}>
                    <span className="driver-bullet"></span>
                    <span className="driver-text">{driver}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Data Coverage */}
        <div className="risk-detail-section">
          <h3 className="section-title">Data Coverage</h3>
          <div className="data-coverage-row">
            {dataCoverage.map((item) => {
              const available = backendCoverage
                ? !!backendCoverage[item.key]
                : clinicalDataTypes.includes(item.key);
              return (
                <div
                  key={item.key}
                  className={`coverage-item ${available ? "active" : ""}`}
                >
                  <img
                    src={available ? circleCheckIcon : circleCrossIcon}
                    alt=""
                    className="coverage-icon"
                  />
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Clinical Data */}
        <div className="risk-detail-section">
          <h3 className="section-title">Clinical Data</h3>

          {vitalSigns.length > 0 && (
            <>
              <h4 className="subsection-title">Vital Signs</h4>
              <div className="clinical-cards-row">
                {vitalSigns.map((item, idx) => (
                  <div
                    key={idx}
                    className="clinical-card"
                    onClick={() => handleCardClick(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="clinical-card-header">
                      <span className="clinical-card-label">
                        {item.paramName}
                      </span>
                      {getTrendIcon(item.tag) === "trending_up" && (
                        <img
                          src={arrowUpRed}
                          alt="trending up"
                          className={`clinical-card-trend-icon ${getTrendClass(item.tag)}`}
                        />
                      )}
                      {getTrendIcon(item.tag) === "trending_down" && (
                        <img
                          src={arrowUpRed}
                          alt="trending down"
                          className={`clinical-card-trend-icon ${getTrendClass(item.tag)}`}
                          style={{ transform: "rotate(180deg)" }}
                        />
                      )}
                    </div>
                    <div className="clinical-card-value">
                      <span className={`value ${getValueColorClass(item.tag)}`}>
                        {item.value} {item.unit}
                      </span>
                      {item.tag && (
                        <span
                          className={`clinical-tag ${getTagClass(item.tag)}`}
                        >
                          {item.tag}
                        </span>
                      )}
                    </div>
                    {item.recordDate && (
                      <div className="clinical-card-date">
                        Recorded on:{" "}
                        {moment(item.recordDate).format("DD MMM YYYY")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {labValues.length > 0 && (
            <>
              <h4 className="subsection-title">Lab Values</h4>
              <div className="clinical-cards-row">
                {labValues.map((item, idx) => (
                  <div
                    key={idx}
                    className="clinical-card"
                    onClick={() => handleCardClick(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="clinical-card-header">
                      <span className="clinical-card-label">
                        {item.paramName}
                      </span>
                      {getTrendIcon(item.tag) === "trending_up" && (
                        <img
                          src={arrowUpRed}
                          alt="trending up"
                          className={`clinical-card-trend-icon ${getTrendClass(item.tag)}`}
                        />
                      )}
                      {getTrendIcon(item.tag) === "trending_down" && (
                        <img
                          src={arrowUpRed}
                          alt="trending down"
                          className={`clinical-card-trend-icon ${getTrendClass(item.tag)}`}
                          style={{ transform: "rotate(180deg)" }}
                        />
                      )}
                    </div>
                    <div className="clinical-card-value">
                      <span className={`value ${getValueColorClass(item.tag)}`}>
                        {item.value} {item.unit}
                      </span>
                      {item.tag && (
                        <span
                          className={`clinical-tag ${getTagClass(item.tag)}`}
                        >
                          {item.tag}
                        </span>
                      )}
                    </div>
                    {item.recordDate && (
                      <div className="clinical-card-date">
                        Recorded on:{" "}
                        {moment(item.recordDate).format("DD MMM YYYY")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {diagnosisData.length > 0 && (
            <>
              <h4 className="subsection-title">Diagnosis History</h4>
              <div className="clinical-cards-row">
                {diagnosisData.map((item, idx) => (
                  <div key={idx} className="clinical-card">
                    <div className="clinical-card-header">
                      <span className="clinical-card-label">
                        {item.paramName}
                      </span>
                      {getTrendIcon(item.tag) === "trending_up" && (
                        <img
                          src={arrowUpRed}
                          alt="trending up"
                          className={`clinical-card-trend-icon ${getTrendClass(item.tag)}`}
                        />
                      )}
                      {getTrendIcon(item.tag) === "trending_down" && (
                        <img
                          src={arrowUpRed}
                          alt="trending down"
                          className={`clinical-card-trend-icon ${getTrendClass(item.tag)}`}
                          style={{ transform: "rotate(180deg)" }}
                        />
                      )}
                    </div>
                    <div className="clinical-card-value">
                      <span className={`value ${getValueColorClass(item.tag)}`}>
                        {item.value} {item.unit}
                      </span>
                      {item.tag && (
                        <span
                          className={`clinical-tag ${getTagClass(item.tag)}`}
                        >
                          {item.tag}
                        </span>
                      )}
                    </div>
                    {item.recordDate && (
                      <div className="clinical-card-date">
                        Recorded on:{" "}
                        {moment(item.recordDate).format("DD MMM YYYY")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {chiefComplaints.length > 0 && (
            <>
              <h4 className="subsection-title">Chief Complaints</h4>
              <div className="clinical-cards-row">
                {chiefComplaints.map((item, idx) => (
                  <div key={idx} className="clinical-card">
                    <div className="clinical-card-header">
                      <span className="clinical-card-label">
                        {item.paramName}
                      </span>
                      {getTrendIcon(item.tag) === "trending_up" && (
                        <img
                          src={arrowUpRed}
                          alt="trending up"
                          className={`clinical-card-trend-icon ${getTrendClass(item.tag)}`}
                        />
                      )}
                      {getTrendIcon(item.tag) === "trending_down" && (
                        <img
                          src={arrowUpRed}
                          alt="trending down"
                          className={`clinical-card-trend-icon ${getTrendClass(item.tag)}`}
                          style={{ transform: "rotate(180deg)" }}
                        />
                      )}
                    </div>
                    <div className="clinical-card-value">
                      <span className={`value ${getValueColorClass(item.tag)}`}>
                        {item.value} {item.unit}
                      </span>
                      {item.tag && (
                        <span
                          className={`clinical-tag ${getTagClass(item.tag)}`}
                        >
                          {item.tag}
                        </span>
                      )}
                    </div>
                    {item.recordDate && (
                      <div className="clinical-card-date">
                        Recorded on:{" "}
                        {moment(item.recordDate).format("DD MMM YYYY")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {hospitalisationData.length > 0 && (
            <>
              <h4 className="subsection-title">Hospitalisation History</h4>
              <div className="clinical-cards-row">
                {hospitalisationData.map((item, idx) => (
                  <div key={idx} className="clinical-card">
                    <div className="clinical-card-header">
                      <span className="clinical-card-label">
                        {item.paramName}
                      </span>
                      {getTrendIcon(item.tag) === "trending_up" && (
                        <img
                          src={arrowUpRed}
                          alt="trending up"
                          className={`clinical-card-trend-icon ${getTrendClass(item.tag)}`}
                        />
                      )}
                      {getTrendIcon(item.tag) === "trending_down" && (
                        <img
                          src={arrowUpRed}
                          alt="trending down"
                          className={`clinical-card-trend-icon ${getTrendClass(item.tag)}`}
                          style={{ transform: "rotate(180deg)" }}
                        />
                      )}
                    </div>
                    <div className="clinical-card-value">
                      <span className={`value ${getValueColorClass(item.tag)}`}>
                        {item.value} {item.unit}
                      </span>
                      {item.tag && (
                        <span
                          className={`clinical-tag ${getTagClass(item.tag)}`}
                        >
                          {item.tag}
                        </span>
                      )}
                    </div>
                    {item.recordDate && (
                      <div className="clinical-card-date">
                        Recorded on:{" "}
                        {moment(item.recordDate).format("DD MMM YYYY")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Recommended Actions */}
        {record.recommendedActions && record.recommendedActions.length > 0 && (
          <div className="risk-detail-section">
            <h3 className="section-title">Recommended Actions</h3>
            <ul className="recommended-actions-list">
              {record.recommendedActions.map((action, idx) => (
                <li key={idx}>
                  <span className="action-bullet"></span>
                  <span className="action-text">{action.action}</span>
                  {action.tags &&
                    action.tags.map((tag, ti) => (
                      <span
                        key={ti}
                        className={`action-tag ${getPriorityClass([tag])}`}
                      >
                        {tag}
                      </span>
                    ))}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Report Evaluation (shown at bottom when no prior evaluation) */}
        {canEvaluate && !evaluationOnTop && evaluationSection}

        {/* Activity Log */}
        <div className="risk-detail-section">
          <button
            type="button"
            className={`activity-log-toggle ${activityLogOpen ? "open" : ""}`}
            onClick={() => setActivityLogOpen((v) => !v)}
            aria-expanded={activityLogOpen}
          >
            <h3 className="section-title">Activity Log</h3>
            <FontIcon className="activity-log-chevron">
              {activityLogOpen ? "expand_less" : "expand_more"}
            </FontIcon>
          </button>
          {activityLogOpen && (
            <div className="activity-log">
              {audits.length === 0 ? (
                <p className="activity-log-empty">No activity yet.</p>
              ) : (
                [...audits]
                  .sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
                  )
                  .map((entry) => {
                    const isSummary =
                      entry.eventType === "evaluation_summary_updated";
                    const isReassign =
                      entry.eventType === "evaluator_reassigned";
                    const subtitle = isReassign
                      ? `${entry.prevName || "-"} > ${entry.subjectName || entry.actorName || "-"}`
                      : entry.actorName || "-";
                    return (
                      <div className="log-entry" key={entry.id}>
                        <div className="log-entry-main">
                          <span className="log-date">
                            {moment(entry.createdAt).format(
                              "DD MMM YYYY, hh:mm A",
                            )}
                          </span>
                          <span className="log-title">
                            {entry.eventLabel || entry.eventType}
                          </span>
                          <span className="log-by">{subtitle}</span>
                        </div>
                        {isSummary && (
                          <button
                            className="log-view-btn"
                            type="button"
                            onClick={() => setComparisonAuditId(entry.id)}
                          >
                            View
                          </button>
                        )}
                      </div>
                    );
                  })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Trend Chart Dialog */}
      <TrendChartDialog
        open={trendChartOpen}
        onClose={() => setTrendChartOpen(false)}
        title={trendChartItem ? `${trendChartItem.paramName} Trend` : ""}
        data={trendData}
        loading={trendLoading}
        unit={trendChartItem?.unit || ""}
        lastValue={
          trendChartItem ? `${trendChartItem.value} ${trendChartItem.unit}` : ""
        }
        status={trendChartItem?.tag || ""}
        statusClass={trendChartItem ? getTagClass(trendChartItem.tag) : ""}
        aiInsight={trendChartItem?.remark || ""}
        patientId={record?.patientId}
      />

      {/* Previous AI Summary Dialog */}
      {prevSummaryOpen && (
        <div
          className="prev-summary-overlay"
          onClick={() => setPrevSummaryOpen(false)}
        >
          <div
            className="prev-summary-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="prev-summary-header">
              <h3>Previous AI summary</h3>
              <span
                className="prev-summary-close"
                onClick={() => setPrevSummaryOpen(false)}
              >
                ✕
              </span>
            </div>
            <div className="prev-summary-body">
              {prevSummaryLoading ? (
                <p className="prev-summary-loading">Loading...</p>
              ) : prevSummaryList.length === 0 ? (
                <p className="prev-summary-empty">
                  No previous assessments found.
                </p>
              ) : (
                prevSummaryList.map((item) => (
                  <div
                    key={item.id}
                    className="prev-summary-item"
                    onClick={() => handlePrevSummaryClick(item)}
                  >
                    <span className="prev-summary-date">
                      {moment(item.triggeredAt).format("D MMM YYYY")}
                    </span>
                    <span
                      className={`prev-summary-risk-badge ${getRiskClass(item.riskLevel)}`}
                    >
                      {getRiskLabel(item.riskLevel) || "-"}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {comparisonAuditId &&
        (() => {
          const summaryAudits = audits.filter(
            (a) => a.eventType === "evaluation_summary_updated",
          );
          const sorted = [...summaryAudits].sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
          );
          const idx = sorted.findIndex((a) => a.id === comparisonAuditId);
          if (idx === -1) return null;
          const latestAudit = sorted[idx];
          const prevAudit = idx > 0 ? sorted[idx - 1] : null;
          return (
            <div
              className="eval-compare-overlay"
              onClick={() => setComparisonAuditId(null)}
            >
              <div
                className="eval-compare-dialog"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="eval-compare-header">
                  <div>
                    <h3 className="eval-compare-title">Evaluation Summary</h3>
                    <p className="eval-compare-subtitle">
                      PID-{record.patientId} ·{" "}
                      {prevAudit
                        ? "Comparing this evaluation with the previous one"
                        : "Comparing this evaluation with the original AI summary"}
                    </p>
                  </div>
                  <button
                    className="eval-compare-close"
                    onClick={() => setComparisonAuditId(null)}
                  >
                    ✕
                  </button>
                </div>
                <div className="eval-compare-body">
                  <div className="eval-compare-col">
                    <div className="eval-compare-col-header prev">
                      <span className="eval-compare-col-label">
                        {prevAudit
                          ? "PREVIOUS EVALUATION"
                          : "ORIGINAL AI SUMMARY"}
                      </span>
                      {prevAudit ? (
                        <>
                          <div className="eval-compare-col-name">
                            {prevAudit.actorName || "-"}
                          </div>
                          <div className="eval-compare-col-date">
                            {moment(prevAudit.createdAt).format(
                              "DD MMM YYYY, hh:mm A",
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="eval-compare-col-name">AI System</div>
                          <div className="eval-compare-col-date">
                            {record.completedAt || record.triggeredAt
                              ? moment(
                                  record.completedAt || record.triggeredAt,
                                ).format("DD MMM YYYY, hh:mm A")
                              : "-"}
                          </div>
                        </>
                      )}
                    </div>
                    {prevAudit
                      ? renderAuditEvalColumn(prevAudit.metadata)
                      : renderAIBaselineColumn(record)}
                  </div>
                  <div className="eval-compare-col">
                    <div className="eval-compare-col-header latest">
                      <span className="eval-compare-col-label">
                        LATEST EVALUATION
                      </span>
                      <div className="eval-compare-col-name">
                        {latestAudit.actorName || "-"}
                      </div>
                      <div className="eval-compare-col-date">
                        {moment(latestAudit.createdAt).format(
                          "DD MMM YYYY, hh:mm A",
                        )}
                      </div>
                    </div>
                    {renderAuditEvalColumn(latestAudit.metadata)}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}

function renderAIBaselineColumn(rec) {
  if (!rec) return null;
  const riskClass = getRiskClass(rec.riskLevel);
  const hasAnyContent =
    rec.riskLevel ||
    rec.riskSummary ||
    (rec.keyDrivers && rec.keyDrivers.length > 0) ||
    (rec.recommendedActions && rec.recommendedActions.length > 0);
  if (!hasAnyContent) {
    return (
      <p className="eval-compare-empty">No AI summary available.</p>
    );
  }
  return (
    <>
      {rec.riskLevel ? (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">RISK LEVEL</div>
          <span className={`risk-badge ${riskClass}`}>
            {getRiskLabel(rec.riskLevel)}
          </span>
        </div>
      ) : null}
      {rec.riskSummary && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">SUMMARY</div>
          <p className="eval-compare-text">{rec.riskSummary}</p>
        </div>
      )}
      {rec.keyDrivers && rec.keyDrivers.length > 0 && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">KEY RISK DRIVERS</div>
          <ul className="eval-compare-list">
            {rec.keyDrivers.map((driver, idx) => (
              <li key={idx}>
                <span className="eval-compare-bullet"></span>
                <span className="eval-compare-list-text">{driver}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {rec.recommendedActions && rec.recommendedActions.length > 0 && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">RECOMMENDATIONS</div>
          <ul className="eval-compare-list">
            {rec.recommendedActions.map((action, idx) => {
              const tags = action.tags || [];
              const category = tags[0];
              const priorities = tags.slice(1);
              return (
                <li key={idx}>
                  <span className="eval-compare-bullet"></span>
                  <div className="eval-compare-list-text">
                    <div>{action.action}</div>
                    {tags.length > 0 && (
                      <div className="eval-compare-tag-row">
                        {category && (
                          <span className="eval-compare-category-pill">
                            → {category}
                          </span>
                        )}
                        {priorities.map((p, pi) => (
                          <span
                            key={pi}
                            className={`eval-compare-priority-pill ${getPriorityClass([p])}`}
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}

function renderAuditEvalColumn(meta) {
  const m = meta || {};
  const riskLevel = m.updatedRiskLevel || 0;
  const riskClass = getRiskClass(riskLevel);
  const hasAnyContent =
    riskLevel ||
    m.evaluatorSummary ||
    m.keyRiskDriversHtml ||
    m.evaluatorRecommendations;
  if (!hasAnyContent) {
    return (
      <p className="eval-compare-empty">
        No snapshot recorded for this audit.
      </p>
    );
  }
  return (
    <>
      {riskLevel ? (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">RISK LEVEL</div>
          <span className={`risk-badge ${riskClass}`}>
            {getRiskLabel(riskLevel)}
          </span>
        </div>
      ) : null}
      {m.evaluatorSummary && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">SUMMARY</div>
          <div
            className="eval-compare-html"
            dangerouslySetInnerHTML={{ __html: m.evaluatorSummary }}
          />
        </div>
      )}
      {m.keyRiskDriversHtml && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">KEY RISK DRIVERS</div>
          <div
            className="eval-compare-html"
            dangerouslySetInnerHTML={{ __html: m.keyRiskDriversHtml }}
          />
        </div>
      )}
      {m.evaluatorRecommendations && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">RECOMMENDATIONS</div>
          <div
            className="eval-compare-html"
            dangerouslySetInnerHTML={{ __html: m.evaluatorRecommendations }}
          />
        </div>
      )}
    </>
  );
}

export default RiskDetail;
